'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from '@bprogress/next/app'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  EditIcon,
  TrashIcon,
  FolderIcon,
  KeyIcon,
  HashIcon,
  LanguagesIcon,
  TextIcon,
  FolderTreeIcon,
  CheckIcon,
  XIcon,
  TagIcon,
  CopyIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { formatDateTime, getLocalizedValue, getTranslatedContentUrl } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CategoryPath,
  ContentDetailInfo,
  ContentDetailPicture,
  ContentError,
  DeleteConfirmationDialog,
  TranslatedContentDetailLink
} from '@/components/partials'
import { useMetaStore } from '@/store'
import { useCategory, useCopyToClipboard, useDeleteCategory } from '@/hooks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type PropsType = {
  id: string
}

const CategoryShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteCategoryId, setDeleteCategoryId] = useState<string | null>(null)

  const { data: category, isLoading, error } = useCategory(id)
  const { metaState } = useMetaStore()
  const locales = metaState.response?.locales
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const routeParams = metaState.response?.routeParams
  const { mutate: deleteCategory, isPending: isDeleting } = useDeleteCategory()

  const getCountryCodeFromLocale = (localeCode: string): string => {
    const parts = localeCode.split('-')
    return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
  }

  const hasTranslation = (localeCode: string): boolean => {
    return category?.translations?.[localeCode] !== undefined
  }

  const handleDelete = () => {
    setDeleteCategoryId(id)
  }

  const confirmDeleteCategory = () => {
    if (deleteCategoryId) {
      deleteCategory(deleteCategoryId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['categories'] })
          await queryClient.invalidateQueries({ queryKey: ['categories-tree'] })
          toast.success(t('deleted_successfully'))
          router.push('/products/categories')
          setDeleteCategoryId(null)
        },
        onError: error => {
          console.error('Error deleting category:', error)
          toast.error(t('error_deleting'))
          setDeleteCategoryId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <CategoryShowSkeleton />
  }

  if (error || !category) {
    return <ContentError error={error} listUrl="/products/categories" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" asChild>
            <Link href="/products/categories">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('category_details')}</h1>
            <p className="text-muted-foreground">{t('view_category_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/products/categories/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('edit_category')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Category Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-6">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {category.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(category.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-4 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FolderTreeIcon className="h-4 w-4" />
                {t('path')}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  <CategoryPath category={category} />
                </Badge>
              </div>
            </div>
            <div className="col-span-2 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <TextIcon className="h-4 w-4" />
                {t('name')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {getLocalizedValue(category, 'name', locale, defaultLocale)}
              </Badge>
            </div>
            <div className="col-span-2 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <KeyIcon className="h-4 w-4" />
                {t('slug')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {getLocalizedValue(category, 'slug', locale, defaultLocale)}
              </Badge>
            </div>
            <div className="col-span-2 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">{t('status')}</div>
              <div className="flex items-center gap-2">
                {category.active ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">{t('active')}</span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">{t('inactive')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={category} />
        </CardContent>
      </Card>

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {t('translations')}
          </CardTitle>
          <CardDescription>{t('available_translations')}</CardDescription>
        </CardHeader>
        <CardContent>
          {locales && locales.length > 0 ? (
            <Tabs defaultValue={locales[0].fullCode} className="w-full">
              <TabsList className="min-h-10.5 w-full">
                {locales.map(localeItem => (
                  <TabsTrigger
                    key={localeItem.fullCode}
                    value={localeItem.fullCode}
                    className="flex items-center gap-2"
                    disabled={!hasTranslation(localeItem.fullCode)}>
                    <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                    <span className="hidden sm:inline">{localeItem.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {locales.map(localeItem => {
                const translatedAppUrl = getTranslatedContentUrl(routeParams, 'products', localeItem.fullCode, category)

                return (
                  <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-6 space-y-4">
                    {hasTranslation(localeItem.fullCode) ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                          <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {localeItem.fullCode}
                          </Badge>
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('name')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <p className="text-sm leading-relaxed">
                                {category.translations[localeItem.fullCode].name}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('slug')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <p className="text-sm leading-relaxed">
                                {category.translations[localeItem.fullCode].slug}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('description')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <div className="text-sm leading-relaxed">
                                {category.translations[localeItem.fullCode].description ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: category.translations[localeItem.fullCode].description || ''
                                    }}
                                  />
                                ) : (
                                  <p>-</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('url')}</label>
                            <TranslatedContentDetailLink href={translatedAppUrl} />
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <label className="text-muted-foreground text-sm font-medium">{t('created_at')}</label>
                              <p className="text-sm">
                                {formatDateTime(category.translations[localeItem.fullCode].createdAt, locale)}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <label className="text-muted-foreground text-sm font-medium">{t('updated_at')}</label>
                              <p className="text-sm">
                                {formatDateTime(category.translations[localeItem.fullCode].updatedAt, locale)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <div className="text-muted-foreground flex items-center justify-center gap-2">
                          <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                          <span>{localeItem.name}</span>
                        </div>
                        <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                      </div>
                    )}
                  </TabsContent>
                )
              })}
            </Tabs>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_languages_available')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pictures */}
      <ContentDetailPicture picture={category.picture} />

      {/* Properties */}
      {category.properties && category.properties.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TagIcon className="h-5 w-5" />
              {t('properties')}
            </CardTitle>
            <CardDescription>{t('category_properties')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {category.properties
                .sort((a, b) => a.order - b.order)
                .map(categoryProperty => {
                  const property = categoryProperty.property

                  let propertyName: string
                  if (property.translations[locale]) {
                    propertyName = property.translations[locale].name || property.key
                  } else {
                    const firstTranslation = Object.values(property.translations)[0]
                    propertyName = firstTranslation?.name || property.key
                  }

                  return (
                    <div
                      key={categoryProperty.id}
                      className="bg-muted/30 flex items-center justify-between rounded-lg border p-4">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="font-mono">
                          {categoryProperty.order}
                        </Badge>
                        <div>
                          <div className="font-medium">{propertyName}</div>
                          <div className="text-muted-foreground text-xs">{property.key}</div>
                        </div>
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {t('fields')}: {property.fields.length}
                      </div>
                    </div>
                  )
                })}
            </div>
          </CardContent>
        </Card>
      )}

      <DeleteConfirmationDialog
        open={deleteCategoryId !== null}
        onCancel={() => setDeleteCategoryId(null)}
        onConfirm={confirmDeleteCategory}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
